import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { getMarkRange, type Editor } from "@tiptap/core";
import { Icon } from "@bedrock/ui";

export interface LinkEditRequest {
  from: number;
  to: number;
  href: string;
}

interface LinkHover extends LinkEditRequest {
  anchor: HTMLElement;
}

const hoverCloseDelay = 220;

export function normalizeLinkHref(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return undefined;
  if (/^mailto:/i.test(trimmed)) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^[a-z][a-z\d+.-]*:/i.test(trimmed) && !/^[^/]+:\d+(\/|$)/.test(trimmed)) return undefined;
  return `https://${trimmed.replace(/^\/\//, "")}`;
}

export function openLink(href: string) {
  window.open(href, "_blank", "noopener,noreferrer");
}

export function linkEditRequestFromSelection(editor: Editor): LinkEditRequest {
  const { selection, doc } = editor.state;
  const linkType = editor.schema.marks["link"];
  if (linkType) {
    const range = getMarkRange(doc.resolve(selection.from), linkType);
    if (range && (selection.empty || selection.to <= range.to)) {
      const href = (editor.getAttributes("link")["href"] as string | undefined) ?? "";
      return { from: range.from, to: range.to, href };
    }
  }
  return { from: selection.from, to: selection.to, href: "" };
}

function anchorPoint(editor: Editor, request: LinkEditRequest): { top: number; left: number } {
  try {
    const start = editor.view.coordsAtPos(request.from);
    const end = editor.view.coordsAtPos(request.to);
    return { top: Math.max(start.bottom, end.bottom), left: start.left };
  } catch {
    const rect = editor.view.dom.getBoundingClientRect();
    return { top: rect.top + 32, left: rect.left };
  }
}

function applyLink(editor: Editor, request: LinkEditRequest, href: string) {
  const chain = editor.chain().focus();
  if (request.from === request.to) {
    chain.insertContentAt(request.from, { type: "text", text: href, marks: [{ type: "link", attrs: { href } }] }).run();
    return;
  }
  chain.setTextSelection({ from: request.from, to: request.to }).setLink({ href }).setTextSelection(request.to).run();
}

function removeLink(editor: Editor, request: LinkEditRequest) {
  editor.chain().focus().setTextSelection({ from: request.from, to: request.to }).unsetLink().setTextSelection(request.to).run();
}

function clampToViewport(element: HTMLElement | null, point: { top: number; left: number }) {
  if (!element) return;
  const margin = 12;
  const width = element.offsetWidth;
  const left = Math.max(margin, Math.min(point.left, window.innerWidth - width - margin));
  const top = point.top + element.offsetHeight + 6 > window.innerHeight - margin
    ? Math.max(margin, point.top - element.offsetHeight - 30)
    : point.top + 6;
  element.style.left = `${left}px`;
  element.style.top = `${top}px`;
}

function LinkEditor({ editor, request, onClose }: { editor: Editor; request: LinkEditRequest; onClose: () => void }) {
  const ref = useRef<HTMLFormElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(request.href);
  const existing = request.href !== "";
  const normalized = normalizeLinkHref(value);

  useLayoutEffect(() => {
    clampToViewport(ref.current, anchorPoint(editor, request));
    input.current?.focus();
    input.current?.select();
  }, [editor, request]);

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && ref.current?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener("pointerdown", closeOutside, true);
    return () => document.removeEventListener("pointerdown", closeOutside, true);
  }, [onClose]);

  const finish = (action?: () => void) => {
    onClose();
    action?.();
    if (!action) editor.commands.focus();
  };
  const submit = () => {
    if (!value.trim() && existing) return finish(() => removeLink(editor, request));
    if (normalized) finish(() => applyLink(editor, request, normalized));
  };
  const handleKeyDown = (event: ReactKeyboardEvent) => {
    event.stopPropagation();
    if (event.key === "Escape" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      finish();
    }
  };

  return createPortal(<form
    ref={ref}
    className="bedrock-link-menu bedrock-link-menu--editing"
    role="dialog"
    aria-label={existing ? "Edit link" : "Add link"}
    onKeyDown={handleKeyDown}
    onSubmit={(event) => { event.preventDefault(); submit(); }}
  >
    <Icon name="link"/>
    <input
      ref={input}
      type="text"
      inputMode="url"
      autoComplete="off"
      spellCheck={false}
      value={value}
      placeholder="Paste or type a link"
      aria-label="Link address"
      aria-invalid={value.trim() !== "" && !normalized}
      onChange={(event) => setValue(event.target.value)}
    />
    <button type="submit" className="bedrock-link-menu__text-button" disabled={!normalized && !(existing && !value.trim())}>{existing && !value.trim() ? "Remove" : "Apply"}</button>
    {existing && <button type="button" aria-label="Remove link" title="Remove link" onClick={() => finish(() => removeLink(editor, request))}><Icon name="close"/></button>}
  </form>, document.body);
}

function LinkHoverCard({ hover, onEdit, onRemove, onPointerEnter, onPointerLeave }: {
  hover: LinkHover;
  onEdit: (request: LinkEditRequest) => void;
  onRemove: (request: LinkEditRequest) => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const rect = hover.anchor.getBoundingClientRect();
    clampToViewport(ref.current, { top: rect.bottom, left: rect.left });
  }, [hover]);

  const request = { from: hover.from, to: hover.to, href: hover.href };
  return createPortal(<div
    ref={ref}
    className="bedrock-link-menu"
    role="group"
    aria-label="Link"
    onPointerEnter={onPointerEnter}
    onPointerLeave={onPointerLeave}
  >
    <button type="button" className="bedrock-link-menu__address" title={hover.href} onClick={() => openLink(hover.href)}>
      <Icon name="external-link"/>
      <span>{hover.href}</span>
    </button>
    <span aria-hidden="true"/>
    <button type="button" className="bedrock-link-menu__text-button" onClick={() => onEdit(request)}>Edit</button>
    <button type="button" className="bedrock-link-menu__text-button" onClick={() => onRemove(request)}>Remove</button>
  </div>, document.body);
}

/** Shows an address card while hovering a link and the address editor on request, so links never navigate on click. */
export function LinkMenu({ editor, editRequest, onEditRequest }: {
  editor: Editor;
  editRequest: LinkEditRequest | null;
  onEditRequest: (request: LinkEditRequest | null) => void;
}) {
  const [hover, setHover] = useState<LinkHover | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const cancelClose = () => clearTimeout(closeTimer.current);
    const scheduleClose = () => {
      cancelClose();
      closeTimer.current = setTimeout(() => setHover(null), hoverCloseDelay);
    };
    const linkType = editor.schema.marks["link"];
    const dom = editor.view.dom;

    const handleOver = (event: MouseEvent) => {
      const anchor = event.target instanceof Element ? event.target.closest<HTMLElement>("a[href]") : null;
      if (!anchor || !dom.contains(anchor) || !linkType) return;
      cancelClose();
      const position = editor.view.posAtDOM(anchor, 0);
      const range = getMarkRange(editor.state.doc.resolve(position), linkType);
      const href = anchor.getAttribute("href");
      if (!range || !href) return;
      setHover((current) => current?.anchor === anchor ? current : { anchor, from: range.from, to: range.to, href });
    };
    const handleOut = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest("a[href]")) scheduleClose();
    };
    const dismiss = () => { cancelClose(); setHover(null); };

    dom.addEventListener("mouseover", handleOver);
    dom.addEventListener("mouseout", handleOut);
    dom.addEventListener("keydown", dismiss);
    dom.addEventListener("scroll", dismiss, true);
    window.addEventListener("scroll", dismiss, true);
    return () => {
      cancelClose();
      dom.removeEventListener("mouseover", handleOver);
      dom.removeEventListener("mouseout", handleOut);
      dom.removeEventListener("keydown", dismiss);
      dom.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("scroll", dismiss, true);
    };
  }, [editor]);

  useEffect(() => {
    if (!hover) return;
    const stale = () => !hover.anchor.isConnected || editor.state.doc.nodeSize <= hover.to;
    const onUpdate = () => { if (stale()) setHover(null); };
    editor.on("update", onUpdate);
    return () => { editor.off("update", onUpdate); };
  }, [editor, hover]);

  if (editRequest) return <LinkEditor key={`${editRequest.from}:${editRequest.to}`} editor={editor} request={editRequest} onClose={() => onEditRequest(null)}/>;
  if (!hover) return null;
  return <LinkHoverCard
    hover={hover}
    onEdit={(request) => { setHover(null); onEditRequest(request); }}
    onRemove={(request) => { setHover(null); removeLink(editor, request); }}
    onPointerEnter={() => clearTimeout(closeTimer.current)}
    onPointerLeave={() => { closeTimer.current = setTimeout(() => setHover(null), hoverCloseDelay); }}
  />;
}
